import { MILITARY_FORMAT } from '@helpers/datetime';
import { getEstimatedFee } from '@helpers/getEstimatedFee';
import {
  eventDuration,
  isAmericanExpress,
  serviceFee,
  shiftDuration,
  shiftRate,
  totalEventRate,
} from '@helpers/post';
import { rateTypeNameFinder } from '@helpers/rateTypeName';
import { get, groupBy } from 'lodash';
import moment from 'moment';
import { useState } from 'react';

export const EstimatedCost = ({ shifts, shiftLocation, rateTypes, payment, currentUser }) => {
  const [showBreakdown, setShowBreakdown] = useState(true);
  const payRate = get(shiftLocation, 'payRate', 0);
  const rateTypeId = get(shiftLocation, 'rateTypeId', 0);
  const maxStartTime = Math.max(...shifts.map(({ startTime }) => moment(startTime, MILITARY_FORMAT)));

  const totalCapacity = shifts.reduce((acc, shift) => acc + shift.capacity, 0);
  const totalDuration = eventDuration({ shifts });
  const totalRate = totalEventRate({ shifts });

  const fee = getEstimatedFee(maxStartTime, 0, isAmericanExpress(payment));
  const locationServiceFee = serviceFee({ shifts, payment, currentUser });
  const estimatedTotal = locationServiceFee + totalRate;

  const displayRate = () => {
    const type = rateTypeNameFinder(rateTypes, rateTypeId);
    return `${payRate}/${type}`;
  };

  const setPrecision = amount => Number(amount).toFixed(2);
  const isHourly = () => Number(rateTypeId) === 0;

  const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

  const shiftGroups = Object.entries(groupBy(shifts, shiftDuration)).map(([duration, group]) => ({
    duration,
    capacity: group.reduce((acc, shift) => acc + shift.capacity, 0),
    rate: group.reduce((acc, shift) => acc + shiftRate(shift), 0),
  }));

  const estimatedTotalCopy = () => {
    const items = [pluralize(totalCapacity, 'AT', 'ATs'), pluralize(shifts.length, 'shift', 'shifts')];
    if (isHourly()) items.push(pluralize(totalDuration, 'hour', 'hours'));

    const schedule = items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items.at(-1)}` : items[0];

    return `Estimate uses the ${schedule} in your schedule. You are billed for hours actually worked.`;
  };

  return (
    <div className="estimated-costs">
      <div className="estimated-total-container">
        <p className="estimated-total-header">
          Estimated total for this event{' '}
          <span className="gotham-bold-font">${setPrecision(estimatedTotal)}</span>
        </p>
        <span className="breakdown-toggle" onClick={() => setShowBreakdown(!showBreakdown)}>
          {showBreakdown ? 'Hide breakdown' : 'Show breakdown'}
          <i className={`fas ${showBreakdown ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
        </span>
      </div>

      {showBreakdown && (
        <div className="estimated-summary-container">
          {isHourly() ? (
            shiftGroups.map(group => (
              <div className="line" key={group.duration}>
                <p>
                  {group.capacity}
                  {group.capacity === 1 ? 'AT' : 'ATs'} x {group.duration}hrs x {displayRate()}
                </p>
                <span>${setPrecision(group.rate)}</span>
              </div>
            ))
          ) : (
            <div className="line">
              <p>
                {totalCapacity}
                {totalCapacity === 1 ? 'AT' : 'ATs'} x {displayRate()}
              </p>
              <span>${setPrecision(totalRate)}</span>
            </div>
          )}
          <div className="line">
            <p>Go4 service fee ({fee}%)</p>
            <span>${setPrecision(locationServiceFee)}</span>
          </div>

          <div className="horizontal-line" />

          <div className="line gotham-bold-font">
            <p>Estimated total</p>
            <span>${setPrecision(estimatedTotal)}</span>
          </div>

          <p className="estimated-notice">{estimatedTotalCopy()}</p>
        </div>
      )}
    </div>
  );
};
